/**
 * Colour themes. The choice is personal: it is kept in this browser only (nothing is sent to the server),
 * so each player sees their own theme and nobody else's screen changes.
 */
export type ThemeName = "classic" | "pink";

export type ThemeInfo = {
  name: ThemeName;
  /** The name shown in the menus. */
  label: string;
  /** A small two-colour sample for the menus. */
  swatch: string;
};

/**
 * Every theme there is, in the order of the menus. A new theme is one more entry here (plus its stylesheet,
 * see scripts/gen-pink-theme.js); the menus in the lobby and in the game list whatever is in this array.
 * "classic" is the normal look: it has no data-theme on the root element.
 */
export const THEMES: ThemeInfo[] = [
  { name: "classic", label: "Classic", swatch: "linear-gradient(135deg, #f0be50 50%, #1b2a38 50%)" },
  { name: "pink", label: "Pink", swatch: "linear-gradient(135deg, #ff8cc8 50%, #3d1530 50%)" },
];

const STORAGE_KEY = "sh-theme";

function isTheme(value: string | null): value is ThemeName {
  return THEMES.some((t) => t.name === value);
}

export function getTheme(): ThemeName {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : "classic";
  } catch (e) {
    return "classic"; // storage can be blocked (private windows)
  }
}

/** Switches the page to a theme: the stylesheets react to data-theme on the root element. */
export function applyTheme(theme: ThemeName): void {
  if (theme === "classic") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", theme);
  }
}

export function setTheme(theme: ThemeName): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    // The theme still changes for this visit.
  }
  applyTheme(theme);
}
